import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { createSession } from '../../../api/sessions';
import { useCertStore } from '../../../stores/certStore';
import type { CreateSessionPayload, SessionSpec } from '../../../types';

function generateSeed(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Launch a study session from the Study Mix screen (plan block, domain tile,
 * or resume) and route into the existing question UI at `/study/session/:id`.
 *
 * This is the same create-then-navigate flow the legacy `SessionLauncher`
 * used — relocated so the Mix entry points share one path into study.
 */
export function useLaunchSession() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const certCode = useCertStore((s) => s.certCode);
  useTranslation(); // subscribe to language changes so names re-localize

  const mutation = useMutation({
    mutationFn: (payload: CreateSessionPayload) => createSession(payload, certCode),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
      navigate(`/study/session/${session.id}`);
    },
  });

  /** Launch a study session with optional topic/difficulty filters. */
  const launch = (opts: { name: string; topicFilter?: string; difficultyFilter?: string }) => {
    mutation.mutate({
      session_name: opts.name,
      mode: 'study',
      topic_filter: opts.topicFilter,
      difficulty_filter: opts.difficultyFilter,
      seed: generateSeed(),
      page_size: 25,
      total_questions: 25,
    });
  };

  /**
   * One-click "smart session": an adaptive set targeting the learner's weak
   * topics (falls back to all topics server-side if none qualify yet).
   */
  const launchSmart = (name: string, size = 25) => {
    const seed = generateSeed();
    const spec: SessionSpec = {
      v: 1,
      certCode,
      mode: 'study',
      topics: [],
      subtopics: [],
      difficulties: [],
      questionTypes: [],
      count: size,
      smart: { onlyWrong: false, onlyUnseen: false, weakTopics: true },
      sessionName: name,
      seed,
    };
    mutation.mutate({
      session_name: name,
      mode: 'study',
      seed,
      page_size: 25,
      total_questions: size,
      filter_spec: spec,
    });
  };

  const defaultName = (label: string) => {
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    const date = new Date().toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    return `${label} · ${date}`;
  };

  return { launch, launchSmart, defaultName, isPending: mutation.isPending, isError: mutation.isError };
}
